import sanitizeHtml from 'sanitize-html';
import { Attachment } from '@app-types/api/attachment';
import { removeCommentHtml } from './parser';

const CID_SRC_PATTERN = /src\s*=\s*(["']?)cid:([^"'>\s]+)\1/gi;

const getCidFileName = (contentId: string): string => {
  const decoded = decodeURIComponent(contentId).replace(/^<|>$/g, '');
  return decoded.split('@')[0].trim().toLowerCase();
};

const findAttachmentByCid = (
  contentId: string,
  attachments: Attachment[]
): Attachment | undefined => {
  const fileName = getCidFileName(contentId);
  return attachments.find(attachment => {
    const attachmentName = attachment.fileName?.trim().toLowerCase();
    if (!attachmentName) {
      return false;
    }

    return (
      attachmentName === fileName ||
      attachmentName.includes(fileName) ||
      fileName.includes(attachmentName)
    );
  });
};

const arrayBufferToDataUrl = (
  buffer: ArrayBuffer,
  mimeType: string
): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return `data:${mimeType};base64,${btoa(binary)}`;
};

export const sanitizeEmailBodyHtml = (body: string): string =>
  sanitizeHtml(body, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      img: ['src', 'alt', 'width', 'height', 'style', 'border', 'id', 'class'],
    },
    allowedSchemes: sanitizeHtml.defaults.allowedSchemes.concat([
      'data',
      'blob',
    ]),
  });

export interface PreparedEmailBodyResult {
  body: string;
  objectUrls: string[];
  inlineAttachmentFileIds: number[];
}

export const excludeInlineAttachments = <T extends Attachment>(
  attachments: T[],
  inlineAttachmentFileIds: number[]
): T[] => {
  if (!inlineAttachmentFileIds.length) {
    return attachments;
  }

  const inlineIds = new Set(inlineAttachmentFileIds);
  return attachments.filter(
    attachment => !attachment.fileId || !inlineIds.has(attachment.fileId)
  );
};

export const replaceInlineCidImages = async (
  body: string,
  attachments: Attachment[],
  loadImage: (
    attachment: Attachment
  ) => Promise<{ file: ArrayBuffer; type: string } | undefined>
): Promise<PreparedEmailBodyResult> => {
  if (!body || !attachments?.length) {
    return { body, objectUrls: [], inlineAttachmentFileIds: [] };
  }

  const contentIds = new Set<string>();
  for (const match of body.matchAll(CID_SRC_PATTERN)) {
    contentIds.add(match[2]);
  }

  if (!contentIds.size) {
    return { body, objectUrls: [], inlineAttachmentFileIds: [] };
  }

  let updatedBody = body;
  const inlineAttachmentFileIds: number[] = [];

  for (const contentId of contentIds) {
    const attachment = findAttachmentByCid(contentId, attachments);
    if (!attachment?.fileId) {
      continue;
    }

    const fileData = await loadImage(attachment);
    if (!fileData?.file) {
      continue;
    }

    const mimeType =
      fileData.type || attachment.mimeType || 'application/octet-stream';
    const dataUrl = arrayBufferToDataUrl(fileData.file, mimeType);

    const cidReference = new RegExp(
      `cid:${contentId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
      'gi'
    );
    updatedBody = updatedBody.replace(cidReference, dataUrl);
    inlineAttachmentFileIds.push(attachment.fileId);
  }

  return { body: updatedBody, objectUrls: [], inlineAttachmentFileIds };
};

export const prepareEmailBodyHtml = async (
  rawBody: string,
  attachments: Attachment[],
  loadImage: (
    attachment: Attachment
  ) => Promise<{ file: ArrayBuffer; type: string } | undefined>
): Promise<PreparedEmailBodyResult> => {
  const withoutComments = removeCommentHtml(rawBody);
  const {
    body: withInlineImages,
    objectUrls,
    inlineAttachmentFileIds,
  } = await replaceInlineCidImages(withoutComments, attachments, loadImage);

  return {
    body: sanitizeEmailBodyHtml(withInlineImages),
    objectUrls,
    inlineAttachmentFileIds,
  };
};

export const revokeInlineImageUrls = (objectUrls: string[]): void => {
  objectUrls.forEach(url => URL.revokeObjectURL(url));
};
